import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { posterService } from "../../api/posterService";
import { STYLE_META, type LongImageBlockForm } from "./blocks";

/**
 * 这一稿**实际发给引擎的那份内容**(2026-08-08)。
 *
 * ═══ 为什么需要它 ═══════════════════════════════════════════════════════════
 * 结果页此前**拿不到块的 props** —— 分段面板的块信息全是从 `asset.segment` /
 * `asset.blockId` + 前端 `STYLE_META` 拼出来的,只有「这块叫什么、什么样式」,
 * 没有「这块上写的是什么字」。于是:
 *   · 「改这一块的字」渲不出表单;
 *   · 「回搭建器改这一稿」只能去翻搭建器草稿 —— 而那份草稿是**按人存一份**的
 *     (`useCanvasBuilder` 拉 listDrafts 取第一份能解析的,与 taskId 无关),
 *     用户出过图 A 又新做了一稿 B,从 A 点回去躺着的是 B。所以它从来没回填过。
 *
 * 从**任务**反解才是对的:那份 payload 就是这张图的生成真相。
 *
 * ★逐块验型再放行:payload 是库里存的 JSON,存量稿 / 半截稿都可能不合形状。
 *   验不过的块**整块丢掉**(不半份恢复)—— 拿一个 props 缺失的块去渲表单,
 *   用户填完保存会把真内容覆盖成半截,比不给这个功能糟得多。
 */
export interface LongImageSpec {
  /** blockId → 这一块的定义;拿不到就是空表(调用方据此降级,不是报错)。 */
  blockById: Map<string, LongImageBlockForm>;
  /** 建这一稿时填的「特别要求」原文。 */
  notes: string | null;
  /** 整包(canvas / style / provider / blocks…),回搭建器要用。 */
  raw: Record<string, unknown> | null;
  isLoading: boolean;
}

/** 一个块能不能拿去渲表单 —— 缺任何一样都不放行(见文件头「不半份恢复」)。 */
function parseBlock(raw: unknown): LongImageBlockForm | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Record<string, unknown>;
  const id = b.id;
  const styleTag = b.styleTag;
  const primitive = b.primitive;
  if (typeof id !== "string" || !id) return null;
  if (typeof styleTag !== "string") return null;
  // ★样式必须是前端认识的:BlockInspector 按 styleTag 分支渲表单,
  //   不认识的 styleTag 会渲出一个什么都没有的空面板(看起来像"这块没内容")。
  const meta = STYLE_META[styleTag];
  if (!meta) return null;
  if (primitive !== meta.primitive) return null; // 原语与样式对不上 = 脏数据
  if (!b.props || typeof b.props !== "object" || Array.isArray(b.props)) return null;
  return {
    id,
    primitive: meta.primitive,
    styleTag,
    name: typeof b.name === "string" ? b.name : "",
    props: b.props as Record<string, unknown>,
  };
}

/**
 * E-09b:这一稿里被用户删掉(hidden)的块 id 集合。
 * 分段资产按契约永不真删,「这块还在不在图里」的唯一真相就是 payload 里的 hidden 标记 ——
 * 分段列表过滤、跳转定位算高度都认它。raw 拿不到 → 空集(降级为不过滤)。
 */
export function hiddenBlockIdsOf(raw: Record<string, unknown> | null | undefined): Set<string> {
  const ids = new Set<string>();
  const rawBlocks = raw?.blocks;
  if (Array.isArray(rawBlocks)) {
    for (const b of rawBlocks) {
      const rec = b as Record<string, unknown> | null;
      if (rec && rec.hidden === true && typeof rec.id === "string") ids.add(rec.id);
    }
  }
  return ids;
}

export function useLongImageSpec(taskId: number | null, enabled: boolean): LongImageSpec {
  const { data, isLoading } = useQuery({
    queryKey: ["longimage-spec", taskId],
    queryFn: () => posterService.readLongImageSpec(taskId as number),
    enabled: enabled && taskId != null,
    staleTime: 30_000,
    /* 404 / 「不是详情长图」不重试:那是稳定结论,重试三次只是拖慢降级。 */
    retry: false,
  });

  return useMemo(() => {
    const blockById = new Map<string, LongImageBlockForm>();
    const rawBlocks = data?.blocks;
    if (Array.isArray(rawBlocks)) {
      for (const raw of rawBlocks) {
        const parsed = parseBlock(raw);
        if (parsed) blockById.set(parsed.id, parsed);
      }
    }
    return {
      blockById,
      notes: typeof data?.notes === "string" ? data.notes : null,
      raw: (data as Record<string, unknown> | undefined) ?? null,
      isLoading,
    };
  }, [data, isLoading]);
}
